import type { BrowserOptions } from '@sentry/react'
import { decideEvent } from './eventDecision'
import { DENIED_URLS, IGNORED_ERROR_MESSAGES } from './noiseRules'
import { scrubBreadcrumb, scrubEvent, scrubTransaction } from './scrub'

const REPORTING_ENVIRONMENTS = ['prod', 'beta']

export const isReportingEnvironment = (frontEnv: string | undefined): boolean =>
  !!frontEnv && REPORTING_ENVIRONMENTS.includes(frontEnv)

/**
 * Must match the release name @sentry/vite-plugin uploads source maps under
 * (see vite.config.mts), or release health and regression detection see two
 * unrelated releases.
 */
export const buildReleaseName = (version: string | undefined): string => `worldfavor-app@${version || 'unknown'}`

export interface SentryEnvironment {
  dsn: string
  frontEnv: string | undefined
  version: string | undefined
  apiUrl: string | undefined
}

const apiPropagationTarget = (apiUrl: string | undefined): (string | RegExp)[] => {
  if (!apiUrl) return []

  try {
    return [new URL(apiUrl).origin]
  } catch {
    return []
  }
}

export const buildInitOptions = (environment: SentryEnvironment): BrowserOptions => ({
  dsn: environment.dsn,
  enabled: isReportingEnvironment(environment.frontEnv),
  environment: environment.frontEnv,
  release: buildReleaseName(environment.version),

  // Cheaper than beforeSend: the SDK never builds an event for these at all.
  ignoreErrors: [...IGNORED_ERROR_MESSAGES],
  denyUrls: [...DENIED_URLS],

  // Sends sentry-trace and baggage headers to our API so a browser error and the
  // backend exception it caused land in one trace. internal-api allows any request
  // header and samples traces at 0.1, so both ends already line up.
  tracePropagationTargets: [/^\//, ...apiPropagationTarget(environment.apiUrl)],
  // Also the fraction of errors that arrive with a usable trace behind them, since an
  // unsampled trace keeps its id but stores no spans. Lower this and error triage loses
  // the backend half of the picture.
  tracesSampleRate: 0.2,

  // Replays are the main source of "what did the user actually do", but recording a
  // fifth of all sessions spends the quota on sessions that never fail.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  sendDefaultPii: false,
  maxBreadcrumbs: 50,
  maxValueLength: 500,
  normalizeDepth: 4,

  beforeBreadcrumb: scrubBreadcrumb,

  beforeSendTransaction: scrubTransaction,

  beforeSend: (event, hint) => {
    const decision = decideEvent(event, hint?.originalException)

    return decision.drop ? null : scrubEvent(decision.event)
  },
})
